<template>
  <view-wrapper
    name="Order"
    :show-back-button="false"
    :loading="loading"
    :loading-text="loadingText"
  >
    <wrapper class="mb-4">
      <g-input
        v-model="orderReference"
        label-placeholder="Order reference"
        disabled
      />
    </wrapper>

    <wrapper class="mb-4" :focus="true">
      <div class="flex justify-between">
        <p>Order data</p>
        <p
          class="font-bold underline cursor-pointer"
          @click="orderDataExpanded = !orderDataExpanded"
        >
          {{ orderDataExpanded ? "Collapse" : "Expand" }}
        </p>
      </div>
      <div v-if="order.orderData" v-show="orderDataExpanded">
        <div
          v-for="(data, index) in order.orderData"
          :key="`order-data--${index}`"
          class="grid grid-cols-5 gap-4"
        >
          <g-input
            :id="`order-key--${index}`"
            type="text"
            label-placeholder="Key"
            v-model="data.key"
            class="col-span-1"
            disabled
          />
          <g-input
            :id="`order-data--${index}`"
            :type="data.propertyType"
            :readonly="!data.editable"
            label-placeholder="Data"
            v-model="data.data"
            :editable="true"
            @input="updateEditableOrderData(data.key, $event)"
            :maxlength="findMaxLength(data)"
            :is-expandable="true"
            class="col-span-4"
          />
        </div>
      </div>
    </wrapper>

    <wrapper class="mb-4 align" v-if="this.features.quickPrintEnabled">
      <g-button class="primary" @click="saveDataForQuickPrint()"
        >Save data for QuickPrint</g-button
      >
    </wrapper>

    <h2 class="mt-6">Articles</h2>
    <wrapper class="mb-4" :focus="true">
      <div class="flex justify-between">
        <p>Article data</p>
        <p
          class="font-bold underline cursor-pointer"
          @click="articleDataExpanded = !articleDataExpanded"
        >
          {{ articleDataExpanded ? "Collapse" : "Expand" }}
        </p>
      </div>
      <div v-if="articles" v-show="articleDataExpanded">
        <div class="flex justify-between">
          <p>&nbsp;</p>
          <p
            class="font-bold underline cursor-pointer"
            @click="showAllArticleData = !showAllArticleData"
          >
            {{ showAllArticleData ? "Show only editable" : "Show all" }}
          </p>
        </div>

        <wrapper
          class="mb-4"
          v-for="(article, articleIndex) in articles"
          :key="`article-data--${articleIndex}`"
        >
          <p>{{ article.name }}</p>
          <div v-if="article.articleData" class="mb-8">
            <div
              v-for="(data, index) in article.articleData"
              v-show="(data.editable && !data.isExtra) || showAllArticleData"
              :key="`editable-article-data--${articleIndex}-${index}`"
              class="grid grid-cols-5 gap-4"
            >
              <g-input
                :id="`editable-key--${articleIndex}-${index}`"
                type="text"
                label-placeholder="Key"
                v-model="data.key"
                class="col-span-1 edit"
                disabled
              />
              <g-input
                :id="`editable-data--${articleIndex}-${index}`"
                :type="data.propertyType"
                label-placeholder=""
                v-model="data.data"
                @input="updateEditableData(articleIndex, data.key, $event)"
                :readonly="!data.editable"
                :maxlength="findMaxLength(data)"
                :editable="data.editable"
                :is-expandable="true"
                class="col-span-4"
              />
            </div>
          </div>
        </wrapper>
      </div>
    </wrapper>

    <wrapper class="flex flex-col mb-4">
      <span v-if="remainingTotal > 0" class="info">
        <p>Available to print: {{ remainingTotal }}</p>
        <p
          @click="setMaxToAllLabels"
          class="underline cursor-pointer w-1/63 orange"
        >
          Set remaining print amount for each available article(s)
        </p>
      </span>
      <span v-if="articles.length > 1">
        <div class="flex mb-4">
          <number-input
            v-model="setAll"
            label-placeholder="Set print amount for all articles"
            :inline-save="false"
            @inlineSave="setAllQuantity"
          />
        </div>
      </span>
      <div class="flex justify-end mb-4" v-if="articles.length > 1">
        <ArticleSortBy :options="sortOptions" v-model="sortState" />
      </div>
      <div class="preview-section">
        <h4 class="preview-section-title">Preview label(s)</h4>
        <div
          class="flex justify-between px-4 mb-5 rounded labels bg-primary-light"
          v-for="article in sortedArticles"
          :key="`${article.articleId}--${componentKey}`"
        >
          <h4 class="w-1/3">{{ article.name }}</h4>
          <div class="flex items-center justify-end w-1/2">
            <number-input
              v-model="article.quantity"
              :value="article.quantity"
              :id="`${article.articleId}`"
              :ref="`${article.articleId}`"
              label-placeholder="Print amount"
            />
          </div>
          <h4
            @click="fetchPreview(article.articleId)"
            class="underline cursor-pointer w-1/63"
          >
            Preview
          </h4>
        </div>
      </div>
      <g-button
        :disabled="articlesToPrint.length === 0"
        class="ml-auto primary"
        :key="componentKey"
        @click="printerSelectOpen = true"
        >Select printers</g-button
      >
    </wrapper>

    <FeatureZone features="integrationWebHooksEnabled">
      <wrapper class="mb-4">
        <h2 class="mt-6">Work flow</h2>
        <g-input
          v-model="orderStatus"
          label-placeholder="Order status"
          disabled
        />
        <g-button
          v-if="orderStatus !== 'Completed'"
          class="mb-2 button big primary white"
          @click="markOrderCompleted"
          >Set as completed</g-button
        >
      </wrapper>
    </FeatureZone>

    <modal v-if="showPreview" type="success" @close="hidePreview">
      <template slot="body">
        <div
          style="max-width: 800px; max-height: 600px"
          class="relative flex justify-center"
        >
          <img class="block object-contain" :src="currentPreview" alt="label" />
        </div>
      </template>
    </modal>

    <modal
      v-if="quickPrintSaveCompleted"
      @close="quickPrintSaveCompleted = !quickPrintSaveCompleted"
      :type="'success'"
      :heading="'Completed'"
      :description="'Data saved for QuickPrint'"
    >
      <template v-slot:buttons>
        <div>
          <grafokett-button
            class="ml-2 big bg-primary text-textcolor"
            @click="quickPrintSaveCompleted = !quickPrintSaveCompleted"
            >Ok</grafokett-button
          >
        </div>
      </template>
    </modal>

    <div>
      <modal v-if="printerSelectOpen" @close="printerSelectOpen = false">
        <template slot="header">
          <img src="../../assets/images/printer.svg" />
        </template>
        <template slot="body">
          <div
            class="flex justify-between px-4 mb-2 text-white rounded bg-primary-light"
          >
            <h4 class="flex-1 mr-4 text-left">{{ label.name }}</h4>
            <drop-down
              v-if="!isPdfOnly"
              class="flex-1"
              placeholder="Select printer"
              :value="selectedPrinter"
              value-property="name"
              search-prop="name"
              :options="assignedPrinters"
              @input="setPrinter($event)"
            />
          </div>
        </template>
        <template slot="buttons">
          <g-button v-if="!isPdfOnly" class="mt-4 primary" @click="print"
            >Print labels</g-button
          >
          <g-button
            :disabled="!isPrintAllowed"
            v-if="order && isPdfEnabled"
            class="mt-4 ml-4 primary"
            @click="generatePdf"
            >Print to PDF</g-button
          >
          <g-button
            :disabled="sendingToGroup"
            v-if="enablePrintToGroup"
            class="mt-4 ml-4 primary"
            @click="sendToGroup"
            >Send to Group</g-button
          >
        </template>
      </modal>
      <printing-progress
        v-if="printing"
        heading="Printing"
        :phase="printProgress.phase"
        :total="printProgress.total"
        :current="printProgress.current"
        :successes="printProgress.successes"
        :failures="printProgress.failures"
        :currentLabel="printProgress.currentLabel"
      />
      <modal
        v-if="error"
        @close="error = false"
        type="error"
        heading="Error"
        :description="errorText"
      />
      <modal
        v-if="printingComplete"
        @close="closePrintingComplete"
        :type="printProgress.failures > 0 ? 'warning' : 'success'"
        heading="Printing complete"
        :description="printResultDescription"
      >
        <template slot="body">
          <h3>{{ printProgress.failures > 0 ? 'Printing completed with errors' : 'Printing complete' }}</h3>
          <p class="m-0">{{ printResultDescription }}</p>
          <div v-if="printProgress.failures > 0" class="mt-4 text-orange-600">
            <p class="font-semibold">{{ printProgress.failures }} label(s) failed to print.</p>
            <p class="text-sm">Check your printer connection and try again.</p>
          </div>
        </template>
      </modal>
    </div>
  </view-wrapper>
</template>
<script>
import GrafokettButton from "../../components/forms/Button.vue";
import GInput from "../../components/forms/Input";
import NumberInput from "../../components/forms/NumberInput";
import Modal from "../../components/Modal";
import DropDown from "../../components/forms/DropDown";
import GButton from "../../components/forms/Button";
import { forEachAsync } from "../../utils/asyncHelpers";
import FeatureZone from "../../components/FeatureZone";
import ArticleSortBy from "../../components/ArticleSortBy.vue";

import {
  sendToPrinterService,
  findFirstDefaultPrinter,
} from "../../utils/printerHelpers";
import { getVisibleVariables } from "../../utils/labelHelper";
import { getArticleDataVariables } from "../../utils/articleDataHelper";
import {
  ARTICLE_NAME_SORT_KEY,
  SORT_ASCENDING,
  buildSortOptions,
  sortArticles,
  getArticleDataKeys,
  getArticleDataValue,
} from "../../utils/articleSortHelper";
import PrintingProgress from "../../components/PrintingProgress";
export default {
  components: {
    GrafokettButton,
    GInput,
    NumberInput,
    Modal,
    DropDown,
    GButton,
    PrintingProgress,
    FeatureZone,
    ArticleSortBy,
  },
  data: () => ({
    order: {},
    label: {},
    connectorId: null,
    features: {},
    loading: false,
    printerSelectOpen: false,
    orderDataExpanded: true,
    articleDataExpanded: false,
    showAllArticleData: false,
    componentKey: 0, // To force update for modal
    currentPreview: "",
    showPreview: false,
    quickPrintSaveCompleted: false,
    loadingText: "",
    setAll: "",
    selectedPrinter: {},
    sendingToGroup: false,
    printing: false,
    printingComplete: false,
    errorText: "",
    error: "",
    printProgress: {
      phase: "generating",
      total: 0,
      current: 0,
      successes: 0,
      failures: 0,
      currentLabel: ""
    },
    sortState: {
      sortKey: ARTICLE_NAME_SORT_KEY,
      direction: SORT_ASCENDING,
    },
  }),
  watch: {
    setAll: function() {
      this.setAllQuantity();
    },
  },
  methods: {
    async saveDataForQuickPrint() {
      this.loadingText = "Saving data";
      this.loading = true;

      const data = this.order.orderData;
      const articleData = Object.keys(data).map(key => data[key]);
      for (let article of this.order.articles) {
        await this.$store.$api.put("/api/articles/quickprint", {
          labelId: this.labelId,
          articleId: parseInt(article.articleId),
          articleData: articleData,
        });
      }

      this.loading = false;
      this.quickPrintSaveCompleted = true;
    },
    setMaxToAllLabels() {
      this.order.articles.forEach(a => {
        if (a.remainingQuantity) {
          a.quantity = a.remainingQuantity;
        }
      });
      this.componentKey++;
    },
    updateEditableOrderData(key, data) {
      const variable = this.order.orderData.find(
        od => od.key.toLowerCase() === key.toLowerCase()
      );
      if (variable) {
        variable.data = data;
        variable.changed = true;
      }
    },
    updateEditableData(index, key, data) {
      const variable = this.order.articles[index].editableData.find(
        ed => ed.key.toLowerCase() === key.toLowerCase()
      );
      if (variable) {
        variable.data = data;
        variable.changed = true;
      }
    },
    findMaxLength(d) {
      var variable = this.label.variables.find(
        v => v.name.toLowerCase() === d.key.toLowerCase()
      );
      if (variable && variable.propertySize) {
        return variable.propertySize;
      }
      return 2000;
    },
    async getEnabledFeatures() {
      const response = await this.$store.$api.get("/api/features");
      this.features = response.data.data;
    },
    async getData() {
      this.loadingText = "Retrieving order";
      const response = await this.$store.$api.get(
        `/api/orders/print/${this.$route.params.orderId}`
      );
      const labelResponse = await this.$store.$api.get(
        `/api/labels/${this.labelId}`
      );
      this.label = labelResponse.data.data;
      this.order = response.data.data;

      var visibleVariables = getVisibleVariables(this.label);

      this.order.orderData = this.order.orderData.map(od => ({
        ...od,
        propertyType:
          (visibleVariables.find(v => v.name === od.key) &&
            visibleVariables.find(v => v.name === od.key).propertyType) ||
          null,
      }));

      this.order.articles = this.order.articles.map(a => {
        const allData = a.editableData.concat(a.staticData);
        const artData = getArticleDataVariables(
          [this.label],
          this.order.orderData,
          allData
        );
        return {
          ...a,
          quantity: "",
          articleData: artData,
          editableData: artData.filter(a => a.editable),
        };
      });
      this.componentKey++;
    },
    async fetchPreview(articleId) {
      this.loadingText = "Generating preview";
      this.loading = true;
      const articleToPreview = (this.order &&
        this.order.articles &&
        this.order.articles.find(a => a.articleId == articleId)) || {
        editableData: [],
      };

      try {
        const response = await this.$store.$api.post(
          "/api/printjobs/article/preview",
          {
            articleId: articleId,
            labelId: this.labelId,
            editableData: articleToPreview.editableData.map(d => ({
              key: d.key,
              data: d.data,
            })),
            orderData:
              (this.order &&
                this.order.orderData.map(d => ({
                  key: d.key,
                  data: d.data,
                }))) ||
              [],
            orderId: this.order && this.order.orderId,
            connectorId: this.connectorId
          }
        );
        this.showPreview = true;
        this.currentPreview = `data:image/png;base64,${response.data.image}`;
      } catch (e) {
        this.error = true;
        var errorMessage = e && e.data && e.data.Message;
        this.errorText = errorMessage || "Could not generate preview";
      }
      this.loading = false;
    },
    hidePreview() {
      this.showPreview = false;
      this.currentPreview = "";
    },
    setAllQuantity() {
      this.order.articles.forEach(article => {
        article.quantity = this.setAll;
      });
      this.componentKey++;
    },
    setPrinter(printer) {
      this.selectedPrinter = printer;
    },
    openPdf(file) {
      const fileUrl = window.URL.createObjectURL(new Blob([file]));
      const fileLink = document.createElement("a");
      fileLink.target = "_blank";
      fileLink.href = fileUrl;
      fileLink.setAttribute("download", `${this.order.orderReference}.pdf`);
      document.body.appendChild(fileLink);
      fileLink.click();
    },
    mapArticleDataToVariables(articleData = []) {
      return articleData.map(variable => ({
        Name: variable.key || "",
        Type: variable.propertyType || "Unknown",
        Value: variable.data,
      }));
    },
    buildGroupPrintEntries() {
      return this.articlesToPrint.map(article => ({
        articleId: article.articleId,
        ExternalIdentifier: article.externalIdentifier || "",
        OrderReference: this.order.orderReference || "",
        Variables: this.mapArticleDataToVariables(article.articleData),
        Quantity: parseInt(article.quantity, 10) || 1,
        LabelIds: [this.labelId],
        TargetGroupId: null,
      }));
    },
    async sendToGroup() {
      const printJobEntries = this.buildGroupPrintEntries();

      if (printJobEntries.length === 0) {
        return;
      }

      this.sendingToGroup = true;
      this.error = false;
      this.errorText = "";

      try {
        await forEachAsync(printJobEntries, async printJobEntry => {
          await this.$store.dispatch("PRINTJOBS/CREATE_TO_GROUP", {
            articleId: printJobEntry.articleId,
            PrintJobEntries: [
              {
                ExternalIdentifier: printJobEntry.ExternalIdentifier,
                OrderReference: printJobEntry.OrderReference,
                Variables: printJobEntry.Variables,
                Quantity: printJobEntry.Quantity,
                LabelIds: printJobEntry.LabelIds,
                TargetGroupId: printJobEntry.TargetGroupId,
              },
            ],
          });
        });

        await this.$store.dispatch("DATA/CALCULATE_PRINT_COUNT");
        this.printerSelectOpen = false;
      } catch (e) {
        this.error = true;
        this.errorText =
          (e && e.response && e.response.data && e.response.data.Message) ||
          "Could not send print job to group";
      } finally {
        this.sendingToGroup = false;
      }
    },
    async generatePdf() {
      const payload = {
        orderId: this.order.orderId,
        orderData: this.order.orderData,
        labels: this.articlesToPrint.map(a => {
          return {
            labelId: this.labelId,
            quantity: a.quantity,
            articleId: a.articleId,
            editableData: a.editableData,
            printerId: this.selectedPrinter.id,
          };
        }),
      };
      this.printing = true;
      this.printerSelectOpen = false;

      try {
        const response = await this.$store.$api.post(
          "/api/printjobs/pdf",
          payload,
          {
            responseType: "blob",
            headers: {
              "Content-Disposition":
                'attachment; filename="order.pdf"; filename*="order.pdf"',
            },
          }
        );

        this.$store.dispatch("DATA/CALCULATE_PRINT_COUNT");

        this.printing = false;
        this.openPdf(response.data);
        await this.decreasePrintQuantity();
      } catch (e) {
        this.printing = false;
        this.error = true;
        var responseText = await e.response.data.text();
        this.errorText = JSON.parse(responseText).Message;
        this.printerSelectOpen = true;
      }
    },
    async decreasePrintQuantity() {
      const decreaseQtyPayload = {
        orderId: this.order.orderId,
        quantityData: this.articlesToPrint.map(a => {
          return {
            consumedQuantity: a.quantity,
            articleId: a.articleId,
          };
        }),
      };
      await this.$store.$api.post(
        "api/printJobs/article/quantity",
        decreaseQtyPayload
      );
    },
    resetPrintProgress() {
      this.printProgress = {
        phase: "generating",
        total: 0,
        current: 0,
        successes: 0,
        failures: 0,
        currentLabel: ""
      };
    },
    closePrintingComplete() {
      this.printingComplete = false;
      this.resetPrintProgress();
    },
    async print() {
      this.previewImages = [];
      this.resetPrintProgress();
      this.printing = true;
      this.printerSelectOpen = false;

      try {
        let printFiles = [];
        let printJobId;
        const printerHelperUri = this.$store.getters[
          "SETTINGS/PRINTER_HELPER_URI"
        ];

        if (!this.printQueues) {
          await this.$store.dispatch("SETTINGS/GET_PRINT_QUEUES");
        }

        var computerName = this.printQueues && this.printQueues.computerName;
        const payload = {
          orderId: this.order.orderId,
          orderData: this.order.orderData,
          computerName: computerName,
          labels: this.articlesToPrint.map(a => {
            return {
              labelId: this.labelId,
              quantity: a.quantity,
              articleId: a.articleId,
              editableData: a.editableData,
              printerId: this.selectedPrinter.id,
            };
          }),
        };
        const { data } = await this.$store.$api.post(
          "/api/printjobs/order",
          payload
        );
        printFiles = data.data;
        printJobId = data.entityId;

        printFiles.forEach(file => {
          this.previewImages.push({
            name: "Label",
            data: file.previewImage,
          });
        });

        // Switch to printing phase
        this.printProgress.phase = "printing";
        this.printProgress.total = printFiles.length;

        // Collect status updates for batch processing
        const statusUpdates = [];

        await forEachAsync(printFiles, async (printFile, index) => {
          // Update progress
          this.printProgress.current = index + 1;

          try {
            await sendToPrinterService(printerHelperUri, {
              targetAddress: printFile.targetIpAddress,
              port: printFile.targetPort,
              targetPrintQueue: printFile.targetPrintQueue,
              printerFileUrl: printFile.printFileName,
              useLocalPrinterDriver: printFile.useLocalPrinterDriver,
              pdfAsBase64String: printFile.pdfAsBase64String,
              orientation: printFile.orientation,
              scale: printFile.scale,
              side: printFile.side,
              paperSize: printFile.paperSize,
              customPaperSize: printFile.customPaperSize,
            });

            this.printProgress.successes++;
            statusUpdates.push({
              printJobEntryId: printFile.printJobEntryId,
              status: "Success"
            });
          } catch (error) {
            this.printProgress.failures++;
            statusUpdates.push({
              printJobEntryId: printFile.printJobEntryId,
              status: "Error",
              message: "Could not print using printerhelper"
            });
          }
        });

        // Batch update all statuses
        if (statusUpdates.length > 0) {
          try {
            await this.$store.dispatch("PRINTJOBS/UPDATE_PRINTJOBENTRY_STATUS_BATCH", {
              updates: statusUpdates
            });
          } catch (batchError) {
            console.error('[PrintLabelOrder] Batch status update error:', batchError);
          }
        }

        await this.$store.dispatch("PRINTJOBS/UPDATE_PRINTJOB_STATUS", {
          printJobId,
          status: "Processed",
        });
        this.printingComplete = true;
        await this.decreasePrintQuantity();
      } catch (error) {
        this.error = true;
        var errorMessage = error.data && error.data.Message;
        this.errorText = errorMessage;
        this.printerSelectOpen = true;
      } finally {
        this.printing = false;
      }
    },
    async markOrderCompleted() {
      const response = await this.$store.$api.post(
        `/api/orders/${this.order.orderId}/completed`
      );

      this.errorMessage =
        response && response.data && response.data.errorReason;
      if (!this.errorMessage) await this.getData();
    },
  },
  computed: {
    printResultDescription() {
      if (this.printProgress.total === 0) {
        return "Your labels have been processed.";
      }
      if (this.printProgress.failures === 0) {
        return `All ${this.printProgress.successes} label(s) have been successfully printed.`;
      }
      return `${this.printProgress.successes} of ${this.printProgress.total} label(s) printed successfully.`;
    },
    remainingTotal() {
      let qty = 0;
      if (this.order && this.order.articles)
        this.order.articles.forEach(a => {
          qty = qty + a.remainingQuantity;
        });
      return qty;
    },
    orderReference() {
      return this.order.orderReference ? this.order.orderReference : "";
    },
    orderStatus() {
      return this.order.orderStatus ? this.order.orderStatus : "";
    },
    articles() {
      return this.order.articles ? this.order.articles : [];
    },
    printers() {
      return this.$store.getters["PRINTERS/INSTALLED_PRINTERS"];
    },
    enablePrintToGroup() {
      // Prefer order-level backend flag for this route.
      if (this.order && this.order.enablePrintToGroup === true) {
        return true;
      }

      // Fallback to label-level flag for older payloads.
      return this.label && this.label.enablePrintToGroup === true;
    },
    assignedGroupLabels() {
      return this.$store.getters["LABELS/ASSIGNED_LABELS"];
    },
    printQueues() {
      return this.$store.getters["SETTINGS/PRINT_QUEUES"];
    },
    assignedPrinters() {
      var assignedPrinters = this.assignedGroupLabels
        .flatMap(x => x.labels)
        .flatMap(x => x.printers)
        .flatMap(x => x.printerId);
      return this.printers.filter(p => {
        return assignedPrinters.find(x => x == p.id);
      });
    },
    labelId() {
      return this.$route.params.id;
    },
    articlesToPrint() {
      return this.sortedArticles.filter(
        article =>
          article.quantity &&
          article.quantity !== "0" &&
          article.quantity !== ""
      );
    },
    sortOptions() {
      return buildSortOptions(this.articles, article =>
        getArticleDataKeys(article)
      );
    },
    sortedArticles() {
      return sortArticles(
        this.articles,
        this.sortState.sortKey,
        this.sortState.direction,
        (article, key) => getArticleDataValue(article, key)
      );
    },
    isPdfEnabled() {
      if (this.features !== null) return this.features.pdfEnabled;
      return false;
    },
    isPdfOnly() {
      if (this.features !== null) return this.features.pdfOnly;
      return false;
    },
    printCounter() {
      return this.$store.getters["DATA/PRINTEDCOUNT"];
    },
    isPrintAllowed() {
      const { type } = this.$store.getters["SUBSCRIPTIONS/SUBSCRIPTION_DATA"];

      if (type !== "Free") return true;

      if (!this.printCounter) return false;

      const printCalc =
        this.printCounter.allowedPrintJobs - this.printCounter.printedJobs;

      return printCalc >= 0;
    },
  },
  async mounted() {
    this.loading = true;
    await this.getData();
    await this.getEnabledFeatures();
    if (this.printers.length === 0) {
      await this.$store.dispatch("PRINTERS/GET_INSTALLED_PRINTERS");
    }
    if (this.assignedGroupLabels.length === 0) {
      await this.$store.dispatch("LABELS/GET_ASSIGNED_LABELS");
    }
    if (!this.printQueues) {
      await this.$store.dispatch("SETTINGS/GET_PRINT_QUEUES");
    }

    if (!this.selectedPrinter.id) {
      var p = findFirstDefaultPrinter(
        this.assignedPrinters,
        this.assignedGroupLabels,
        this.labelId
      );
      if (p) {
        this.setPrinter(p);
      }
    }

    this.loading = false;
  },
};
</script>

<style scoped>
.info {
  display: flex;
  flex-direction: row;
}
.info p {
  align-self: center;
  padding-left: 15px;
  font-style: italic;
}
.orange {
  color: orange;
}
.preview-section {
  margin-bottom: 20px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.preview-section-title {
  margin: 0 0 12px 0;
  font-size: 1rem;
  font-weight: 600;
}
</style>
